import * as url from "../../helpers/url_helper";
import { apiCall, fetchApi } from "./apiService";

/**
 * Gets merchant info by shop domain
 * @param {string} shop - Shop domain
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Merchant data or null if error
 */
export const getMerchantInfo = async (shop, notifyError) => {
    return await apiCall(
        `${url.MERCHANT_INFO}?store=${encodeURIComponent(shop)}`,
        'GET',
        null,
        notifyError,
        "Failed to load merchant info"
    );
};

/**
 * Gets subscription information
 * @param {string} shop - Shop domain
 * @param {string} subscriptionId - Subscription ID
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Subscription data or null if error
 */
export const getSubscriptionInfo = async (shop, subscriptionId, notifyError) => {
    try {
        const endpoint = `${url.GET_SUBSCRIPTION_INFO}?shop=${encodeURIComponent(shop)}&subscription_id=${subscriptionId || ''}`;
        const { response, results } = await fetchApi(endpoint, 'GET');

        if (response.ok && results?.data) {
            return results.data;
        }
        return null;
    } catch (error) {
        if (notifyError) {
            notifyError("Failed to load subscription info");
        }
        return null;
    }
};

/**
 * Calculates trial days information
 * @param {Object} subscriptionData - Subscription data from API
 * @returns {Object} Trial days calculation results
 */
export const calculateTrialDays = (subscriptionData) => {
    if (!subscriptionData) return { trialDays: 0, isExceedTrialDays: true };

    const createdAt = new Date(subscriptionData.createdAt);
    const periodEnd = new Date(subscriptionData.currentPeriodEnd);
    const diffMs = periodEnd - createdAt;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const trialDays = diffDays === subscriptionData.trialDays ? subscriptionData.trialDays : 0;

    const trialEndsAt = new Date(createdAt);
    trialEndsAt.setDate(trialEndsAt.getDate() + subscriptionData.trialDays);
    const now = new Date();
    const isExceedTrialDays = trialEndsAt < now || trialDays <= 0;

    return { trialDays, isExceedTrialDays };
};

/**
 * Extends trial days for a subscription
 * @param {string} shop - Shop domain
 * @param {number} trialDays - Number of trial days to add
 * @param {string} subscriptionId - Subscription ID
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Response data or null if error
 */
export const extendTrialDays = async (shop, trialDays, subscriptionId, notifyError) => {
    return await apiCall(
        url.EXTEND_TRIAL_DAYS,
        'PUT',
        {
            shop,
            trial_days: trialDays,
            subscription_id: subscriptionId,
        },
        notifyError,
        "Failed to extend trial days"
    );
};

/**
 * Updates usage charges (additional orders price)
 * @param {string} shop - Shop domain
 * @param {number|string} usageCharges - Additional orders price
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Response data or null if error
 */
export const updateUsageCharges = async (shop, usageCharges, notifyError) => {
    return await apiCall(
        url.EDIT_USAGE_CHARGES,
        'PUT',
        {
            shop,
            usage_charges: usageCharges,
        },
        notifyError,
        "Failed to update usage charges"
    );
};

/**
 * Cancels a subscription
 * @param {string} shop - Shop domain
 * @param {string} subscriptionId - Subscription ID
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Response data or null if error
 */
export const cancelSubscription = async (shop, subscriptionId, notifyError) => {
    return await apiCall(
        url.CANCEL_SUBSCRIPTION,
        'POST',
        {
            shop,
            subscription_id: subscriptionId,
        },
        notifyError,
        "Failed to cancel subscription"
    );
};

/**
 * Updates store status (enable/disable or free plan)
 * @param {string} storeDomain - Store domain
 * @param {boolean} isFreeSubscription - Whether it's a free subscription (optional)
 * @param {boolean} isActive - Whether store is active (optional)
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Response data or null if error
 */
export const updateStoreStatus = async (storeDomain, isFreeSubscription, isActive, notifyError) => {
    const body = isFreeSubscription === undefined
        ? { store_domain: storeDomain, is_active: isActive, install_status: isActive ? 3 : 0 }
        : { store_domain: storeDomain, is_free_subscription: isFreeSubscription };

    return await apiCall(
        url.PUT_UPDATE_STORE,
        'PUT',
        body,
        notifyError,
        "Failed to update store status"
    );
};

